<template>
    <div class="embed-youtube">
        <iframe
            v-if="!!cookies.video"
            :id="'youtube-embed-' + video.video_youtube_id"
            class="w-full aspect-video"
            :src="`https://www.youtube.com/embed/${video.video_youtube_id}?enablejsapi=1&autoplay=${autoplayInt}&rel=0`"
            allowfullscreen="allowfullscreen"
            allow="autoplay"
            loading="lazy"
        ></iframe>
        <missing-video v-else class="w-full aspect-video" :poster="video.video_thumb_url"></missing-video>
    </div>
</template>

<script setup>
const props = defineProps({
    video: {
        required: true,
        type: Object,
        default() {
            return {};
        },
    },
    autoplay: {
        required: false,
        type: [Boolean, Number],
        default: false,
    },
});

const {cookies} = storeToRefs(useStoreCookies());

const autoplayInt = computed(() => {
    return props.autoplay ? '1' : '0';
});

let youtube = null;

const emit = defineEmits(['play', 'progress', 'pause', 'destroy', 'end']);

const timer = ref(null);
const totalTime = ref(0);

function startTimer() {
    if (timer.value) return;
    timer.value = setInterval(function () {
        totalTime.value += 1;
        emit('progress', {totalTime: totalTime.value, currentTime: youtube.getPlayerCurrentTime()});
    }, 1000);
}

function stopTimer() {
    if (timer.value) {
        clearInterval(timer.value);
        timer.value = null;
    }
}

function keyListener(e) {
    if (e.code === 'Space') {
        if (youtube.getPlayerStateReadable() === 'playing') {
            youtube.pauseVideo();
        } else {
            youtube.playVideo();
        }
    }
    if (e.code === 'ArrowLeft') {
        const currentTime = youtube.getPlayerCurrentTime();
        youtube.seekTo(currentTime - 5);
    }
    if (e.code === 'ArrowRight') {
        const currentTime = youtube.getPlayerCurrentTime();
        youtube.seekTo(currentTime + 5);
    }
}

onMounted(async () => {
    watch(
        cookies,
        async () => {
            if (cookies.value.video) {
                youtube = usePluginYoutube();
                await youtube.init();
                youtube.attachPlayer('youtube-embed-' + props.video.video_youtube_id, onPlayerUpdate);
            }
        },
        {immediate: true},
    );

    function onPlayerUpdate(state) {
        if (state === 'playing') {
            emit('play', {totalTime: totalTime.value, currentTime: youtube.getPlayerCurrentTime()});
            startTimer();
        } else if (state === 'ended') {
            emit('end', {totalTime: totalTime.value, currentTime: youtube.getPlayerCurrentTime()});
            stopTimer();
        } else {
            emit('pause', {totalTime: totalTime.value, currentTime: youtube.getPlayerCurrentTime()});
            stopTimer();
        }
    }

    await nextTick(() => {
        const element = document.getElementById('youtube-embed-' + props.video.video_youtube_id);
        if (element) element.scrollIntoView({behavior: 'smooth', block: 'center'});
    });

    window.addEventListener('keydown', keyListener);
});

onUnmounted(() => {
    window.removeEventListener('keydown', keyListener);
    if (youtube) emit('destroy', {totalTime: totalTime.value, currentTime: youtube.getPlayerCurrentTime()});
    stopTimer();
});
</script>
